import { Suspense, useEffect, useState } from "react";
import leafLottieUrl from "@/assets/green-leaf-loader.lottie?url";
import { lazyRetry } from "@/lib/lazyRetry";
import { BRAND_GREEN_BRIGHT, BRAND_GREEN_MID } from "@/lib/design-tokens";

const DotLottieReact = lazyRetry(
  () => import("@lottiefiles/dotlottie-react"),
  { pick: "DotLottieReact", name: "@lottiefiles/dotlottie-react:DotLottieReact" },
);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

const StaticLeafFallback = ({ size }: { size: number }) => (
  <svg
    width={size * 0.75}
    height={size * 0.75}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M21 3c0 8-5 13-13 13H3v-3c0-7 5-12 12-12h6Z"
      fill={`var(--color-brand-bright, ${BRAND_GREEN_BRIGHT})`}
      opacity="0.85"
    />
    <path
      d="M3 21c4-8 9-12 17-15"
      stroke={`var(--color-brand, ${BRAND_GREEN_MID})`}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export interface ClimateLeafLogoProps {
  size?: number;
  className?: string;
  ariaLabel?: string;
}

export function ClimateLeafLogo({
  size = 64,
  className,
  ariaLabel = "Carbon removal contribution",
}: ClimateLeafLogoProps) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {reducedMotion ? (
        <StaticLeafFallback size={size} />
      ) : (
        <Suspense fallback={<StaticLeafFallback size={size} />}>
          <DotLottieReact
            src={leafLottieUrl}
            autoplay
            loop
            speed={0.5}
            style={{ width: size, height: size }}
          />
        </Suspense>
      )}
    </span>
  );
}

export default ClimateLeafLogo;
